// AusweisdokumentAbschnitt.tsx — Sektion zur Erfassung von Ausweisdokumenten.
//
// Unterstützt 5 Dokumentarten:
// 1. Personalausweis
// 2. Reisepass
// 3. Aufenthaltstitel (mit Subtyp-Auswahl)
// 4. Führerschein (Kraftfahrzeuge, Sportboot, Luftfahrt mit dynamischen Mehrfachauswahl-Katalogen)
// 5. Geburtsurkunde und Sterbeurkunde (Registernummer, Standesamt — ohne
//    „Gültig bis"; User 28.09.2026)
// 6. Sonstiges Ausweisdokument (mit Freitext-Rückfrage)
//
// Am Eintrag steht nur noch die aus dem Scan vorgemerkte Datei
// (`DokumentEintragAktionen`). Gescannt und hochgeladen wird über den Knopf
// oben; „<Art> scannen" und „Datei hochladen" am Eintrag sind weg (02.10.2026).
//
// Besonderheiten:
// - Zusammengeführter Scan-/Upload-Button oben: „Dokumente scannen / hochladen"
// - Mehrere Dokumente möglich via Button unten rechts: „+ weiteres Dokument hinzufügen"
// - Trennung einzelner Dokumente durch einfachen Divider ohne „Eintrag 1"-Überschrift
// - Regeln:
//   - Wechsel der Dokumentart: Rückfrage (idCardSubtype) und Klassen (idCardClasses) geleert.
//   - Wechsel der Führerschein-Rückfrage: Klassen (idCardClasses) geleert.
//   - Nummer, Datumsangaben und Behörde/Stelle bleiben stets erhalten.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { FieldRow, LabeledInput, LabeledSelect, IconVisitenkarte } from "../formPrimitives";
import { MehrfachSelect } from "./MehrfachSelect";
import { DokumentEintragAktionen } from "./DokumentEintragAktionen";
import {
  AUSWEIS_ARTEN,
  AUFENTHALTSTITEL_ARTEN,
  FUEHRERSCHEIN_BEREICHE,
  KFZ_KLASSEN,
  SPORTBOOT_SCHEINE,
  LUFTFAHRT_LIZENZEN,
  formatClassesInCatalogOrder,
  parseClassesString,
  dokumentFelder,
  GEBURTSURKUNDE,
  STERBEURKUNDE,
} from "../../../data/ausweisarten";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  actionRow: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "4px",
  },
  scanBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "10px 18px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  documentItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    position: "relative",
  },
  divider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("20px", "0"),
    ...shorthands.borderWidth("0"),
  },
  itemTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: "-4px",
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("6px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.80rem",
    padding: "3px 8px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  addBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.86rem",
    padding: "7px 16px",
    cursor: "pointer",
    alignSelf: "flex-end",
    marginTop: "12px",
    transitionProperty: "background-color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  ghostBtn: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 12px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
});

export interface AusweisdokumentEintrag {
  id: string;
  idCardType: string;
  idCardSubtype?: string;
  idCardClasses?: string;
  idCardNumber?: string;
  idCardIssuedAt?: string;
  idCardValidUntil?: string;
  idCardAuthority?: string;
  idCardDocUrl?: string;
  idCardDocName?: string;
}

export interface AusweisdokumentAbschnittProps {
  /** Liste aller erfassten Dokumente */
  documents?: AusweisdokumentEintrag[];
  onDocumentsChange?: (documents: AusweisdokumentEintrag[]) => void;

  /** Fallback für Einzel-Dokument Modus */
  idCardType?: string;
  idCardSubtype?: string;
  idCardClasses?: string;
  idCardNumber?: string;
  idCardIssuedAt?: string;
  idCardValidUntil?: string;
  idCardAuthority?: string;
  onFieldChange?: (field: string, value: string) => void;

  onScanClick: () => void;
  onOpenDocument?: (url: string) => void;

  /** Zum Ablegen vorgemerkte Datei eines Eintrags. */
  dateiName?: (docId: string) => string | undefined;
  onDateiEntfernen?: (docId: string) => void;
}

export const AusweisdokumentAbschnitt: React.FC<AusweisdokumentAbschnittProps> = ({
  documents,
  onDocumentsChange,
  idCardType = "",
  idCardSubtype = "",
  idCardClasses = "",
  idCardNumber = "",
  idCardIssuedAt = "",
  idCardValidUntil = "",
  idCardAuthority = "",
  onFieldChange,
  onScanClick,
  onOpenDocument,
  dateiName,
  onDateiEntfernen,
}) => {
  const s = useStyles();

  // Interner Fallback-State für Einzel-Dokument-Caller
  const [fallbackDocs, setFallbackDocs] = React.useState<AusweisdokumentEintrag[]>([
    {
      id: "doc-1",
      idCardType: idCardType || "Personalausweis",
      idCardSubtype: idCardSubtype || "",
      idCardClasses: idCardClasses || "",
      idCardNumber: idCardNumber || "",
      idCardIssuedAt: idCardIssuedAt || "",
      idCardValidUntil: idCardValidUntil || "",
      idCardAuthority: idCardAuthority || "",
    },
  ]);

  // Wenn von außen Einzel-Felder aktualisiert werden (z. B. im Test), synchronisieren
  React.useEffect(() => {
    if (!documents) {
      setFallbackDocs((prev) => [
        {
          id: prev[0]?.id || "doc-1",
          idCardType: idCardType || "Personalausweis",
          idCardSubtype: idCardSubtype || "",
          idCardClasses: idCardClasses || "",
          idCardNumber: idCardNumber || "",
          idCardIssuedAt: idCardIssuedAt || "",
          idCardValidUntil: idCardValidUntil || "",
          idCardAuthority: idCardAuthority || "",
        },
        ...prev.slice(1),
      ]);
    }
  }, [
    documents,
    idCardType,
    idCardSubtype,
    idCardClasses,
    idCardNumber,
    idCardIssuedAt,
    idCardValidUntil,
    idCardAuthority,
  ]);

  const activeDocs = documents && documents.length > 0 ? documents : fallbackDocs;

  const updateDoc = (id: string, patch: Partial<AusweisdokumentEintrag>) => {
    if (onDocumentsChange && documents) {
      onDocumentsChange(
        documents.map((d) => (d.id === id ? { ...d, ...patch } : d)),
      );
    } else {
      setFallbackDocs((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...patch } : d)),
      );
      if (onFieldChange) {
        Object.entries(patch).forEach(([key, val]) => {
          onFieldChange(key, val ?? "");
        });
      }
    }
  };

  const addDoc = () => {
    const newDoc: AusweisdokumentEintrag = {
      id: "doc-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
      idCardType: "Personalausweis",
      idCardSubtype: "",
      idCardClasses: "",
      idCardNumber: "",
      idCardIssuedAt: "",
      idCardValidUntil: "",
      idCardAuthority: "",
    };
    if (onDocumentsChange && documents) {
      onDocumentsChange([...documents, newDoc]);
    } else {
      setFallbackDocs((prev) => [...prev, newDoc]);
    }
  };

  const removeDoc = (id: string) => {
    if (onDocumentsChange && documents) {
      onDocumentsChange(documents.filter((d) => d.id !== id));
    } else {
      setFallbackDocs((prev) => prev.filter((d) => d.id !== id));
    }
  };

  return (
    <div className={s.container}>
      {/* ── Scan & Upload Button zusammengeführt oben ── */}
      <div className={s.actionRow}>
        <button
          type="button"
          className={s.scanBtn}
          title="Dokumente scannen / hochladen"
          onClick={onScanClick}
        >
          <IconVisitenkarte /> Dokumente scannen / hochladen
        </button>
      </div>

      {/* ── Liste aller Dokumente ── */}
      {activeDocs.map((doc, idx) => {
        const currentType = doc.idCardType || "Personalausweis";
        const selectedClasses = parseClassesString(doc.idCardClasses);

        const getClassesBeschriftung = (placeholder: string): string => {
          if (selectedClasses.length === 0) return placeholder;
          if (selectedClasses.length <= 2) return selectedClasses.join(", ");
          return `${selectedClasses.length} ausgewählt`;
        };

        const handleTypeChange = (newType: string) => {
          updateDoc(doc.id, {
            idCardType: newType,
            idCardSubtype: "",
            idCardClasses: "",
          });
        };

        const handleFuerherscheinBereichChange = (newBereich: string) => {
          updateDoc(doc.id, {
            idCardSubtype: newBereich,
            idCardClasses: "",
          });
        };

        const handleToggleClass = (c: string, catalog: string[]) => {
          const currentSet = new Set(selectedClasses);
          if (currentSet.has(c)) {
            currentSet.delete(c);
          } else {
            currentSet.add(c);
          }
          const formatted = catalog.filter((item) => currentSet.has(item)).join(", ");
          updateDoc(doc.id, { idCardClasses: formatted });
        };

        return (
          <div key={doc.id} className={s.documentItem}>
            {idx > 0 && <hr className={s.divider} />}

            {currentType === "Personalausweis" && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledInput
                    label="Ausweisnummer"
                    name={`idCardNumber-${doc.id}`}
                    value={doc.idCardNumber || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label="Gültig bis"
                    name={`idCardValidUntil-${doc.id}`}
                    type="date"
                    value={doc.idCardValidUntil || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardValidUntil: v })}
                  />
                </FieldRow>
                <LabeledInput
                  label="Ausstellende Behörde"
                  name={`idCardAuthority-${doc.id}`}
                  value={doc.idCardAuthority || ""}
                  onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                  placeholder="Stadt Aachen"
                />
              </>
            )}

            {currentType === "Reisepass" && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledInput
                    label="Passnummer"
                    name={`idCardNumber-${doc.id}`}
                    value={doc.idCardNumber || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label="Gültig bis"
                    name={`idCardValidUntil-${doc.id}`}
                    type="date"
                    value={doc.idCardValidUntil || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardValidUntil: v })}
                  />
                </FieldRow>
                <LabeledInput
                  label="Ausstellende Behörde"
                  name={`idCardAuthority-${doc.id}`}
                  value={doc.idCardAuthority || ""}
                  onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                  placeholder="Stadt Aachen"
                />
              </>
            )}

            {currentType === "Aufenthaltstitel" && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledSelect
                    label="Welcher Aufenthaltstitel?"
                    name={`idCardSubtype-${doc.id}`}
                    value={doc.idCardSubtype || ""}
                    onChange={handleFuerherscheinBereichChange}
                    options={AUFENTHALTSTITEL_ARTEN.map((v) => ({ value: v, label: v }))}
                    placeholder="Aufenthaltstitel wählen"
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Dokumentnummer"
                    name={`idCardNumber-${doc.id}`}
                    value={doc.idCardNumber || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                  />
                  <div />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label="Gültig bis"
                    name={`idCardValidUntil-${doc.id}`}
                    type="date"
                    value={doc.idCardValidUntil || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardValidUntil: v })}
                  />
                </FieldRow>
                <LabeledInput
                  label="Ausstellende Behörde"
                  name={`idCardAuthority-${doc.id}`}
                  value={doc.idCardAuthority || ""}
                  onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                  placeholder="Ausländerbehörde Aachen"
                />
              </>
            )}

            {currentType === "Führerschein" && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledSelect
                    label="Wofür gilt der Führerschein?"
                    name={`idCardSubtype-${doc.id}`}
                    value={doc.idCardSubtype || ""}
                    onChange={handleFuerherscheinBereichChange}
                    options={FUEHRERSCHEIN_BEREICHE.map((v) => ({ value: v, label: v }))}
                    placeholder="Bereich wählen"
                  />
                </FieldRow>

                {doc.idCardSubtype === "Kraftfahrzeuge" && (
                  <FieldRow columns={2}>
                    <LabeledInput
                      label="Führerscheinnummer"
                      name={`idCardNumber-${doc.id}`}
                      value={doc.idCardNumber || ""}
                      onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                    />
                    <MehrfachSelect<string>
                      label="Fahrerlaubnisklassen"
                      name={`idCardClasses-${doc.id}`}
                      options={KFZ_KLASSEN.map((c) => ({ value: c, label: c }))}
                      value={selectedClasses}
                      onToggle={(c) => handleToggleClass(c, KFZ_KLASSEN)}
                      beschriftung={getClassesBeschriftung("Klassen wählen")}
                      emptyText="Keine Klassen gefunden"
                    />
                  </FieldRow>
                )}

                {doc.idCardSubtype === "Sportboot" && (
                  <FieldRow columns={2}>
                    <LabeledInput
                      label="Führerscheinnummer"
                      name={`idCardNumber-${doc.id}`}
                      value={doc.idCardNumber || ""}
                      onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                    />
                    <MehrfachSelect<string>
                      label="Scheine"
                      name={`idCardClasses-${doc.id}`}
                      options={SPORTBOOT_SCHEINE.map((c) => ({ value: c, label: c }))}
                      value={selectedClasses}
                      onToggle={(c) => handleToggleClass(c, SPORTBOOT_SCHEINE)}
                      beschriftung={getClassesBeschriftung("Scheine wählen")}
                      emptyText="Keine Scheine gefunden"
                    />
                  </FieldRow>
                )}

                {doc.idCardSubtype === "Luftfahrt" && (
                  <FieldRow columns={2}>
                    <LabeledInput
                      label="Lizenznummer"
                      name={`idCardNumber-${doc.id}`}
                      value={doc.idCardNumber || ""}
                      onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                    />
                    <MehrfachSelect<string>
                      label="Lizenzen und Berechtigungen"
                      name={`idCardClasses-${doc.id}`}
                      options={LUFTFAHRT_LIZENZEN.map((c) => ({ value: c, label: c }))}
                      value={selectedClasses}
                      onToggle={(c) => handleToggleClass(c, LUFTFAHRT_LIZENZEN)}
                      beschriftung={getClassesBeschriftung("Lizenzen wählen")}
                      emptyText="Keine Lizenzen gefunden"
                    />
                  </FieldRow>
                )}

                {!doc.idCardSubtype && (
                  <FieldRow columns={2}>
                    <LabeledInput
                      label="Führerscheinnummer"
                      name={`idCardNumber-${doc.id}`}
                      value={doc.idCardNumber || ""}
                      onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                    />
                    <div />
                  </FieldRow>
                )}

                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label={
                      doc.idCardSubtype === "Sportboot"
                        ? "Gültig bis (falls befristet)"
                        : "Gültig bis"
                    }
                    name={`idCardValidUntil-${doc.id}`}
                    type="date"
                    value={doc.idCardValidUntil || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardValidUntil: v })}
                  />
                </FieldRow>

                <LabeledInput
                  label={
                    doc.idCardSubtype === "Sportboot"
                      ? "Ausstellende Stelle"
                      : "Ausstellende Behörde"
                  }
                  name={`idCardAuthority-${doc.id}`}
                  value={doc.idCardAuthority || ""}
                  onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                  placeholder={
                    doc.idCardSubtype === "Sportboot"
                      ? "DMYV / DSV"
                      : doc.idCardSubtype === "Luftfahrt"
                      ? "Luftfahrt-Bundesamt"
                      : "Straßenverkehrsamt Aachen"
                  }
                />
              </>
            )}

            {(currentType === GEBURTSURKUNDE || currentType === STERBEURKUNDE) && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledInput
                    label={dokumentFelder(currentType).nummer}
                    name={`idCardNumber-${doc.id}`}
                    value={doc.idCardNumber || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                    placeholder={currentType === GEBURTSURKUNDE ? "G 123/1990" : "S 45/2026"}
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label={dokumentFelder(currentType).behoerde}
                    name={`idCardAuthority-${doc.id}`}
                    value={doc.idCardAuthority || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                    placeholder={dokumentFelder(currentType).behoerdePlatzhalter}
                  />
                </FieldRow>
              </>
            )}

            {currentType === "Sonstiges Ausweisdokument" && (
              <>
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Dokumentart"
                    name={`idCardType-${doc.id}`}
                    value={currentType}
                    onChange={handleTypeChange}
                    options={AUSWEIS_ARTEN.map((v) => ({ value: v, label: v }))}
                  />
                  <LabeledInput
                    label="Welches Dokument?"
                    name={`idCardSubtype-${doc.id}`}
                    value={doc.idCardSubtype || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardSubtype: v })}
                    placeholder="z. B. Schwerbehindertenausweis"
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Dokumentnummer"
                    name={`idCardNumber-${doc.id}`}
                    value={doc.idCardNumber || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardNumber: v })}
                  />
                  <div />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ausgestellt am"
                    name={`idCardIssuedAt-${doc.id}`}
                    type="date"
                    value={doc.idCardIssuedAt || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardIssuedAt: v })}
                  />
                  <LabeledInput
                    label="Gültig bis"
                    name={`idCardValidUntil-${doc.id}`}
                    type="date"
                    value={doc.idCardValidUntil || ""}
                    onChange={(v) => updateDoc(doc.id, { idCardValidUntil: v })}
                  />
                </FieldRow>
                <LabeledInput
                  label="Ausstellende Stelle"
                  name={`idCardAuthority-${doc.id}`}
                  value={doc.idCardAuthority || ""}
                  onChange={(v) => updateDoc(doc.id, { idCardAuthority: v })}
                />
              </>
            )}
            <DokumentEintragAktionen
              art={currentType}
              dateiName={dateiName?.(doc.id)}
              onDateiEntfernen={onDateiEntfernen ? () => onDateiEntfernen(doc.id) : undefined}
            />
            {activeDocs.length > 1 && (
              <div className={s.itemTopBar} style={{ marginTop: "4px" }}>
                <button
                  type="button"
                  className={s.removeBtn}
                  onClick={() => removeDoc(doc.id)}
                >
                  Entfernen
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Button unten rechts: + weiteres Dokument hinzufügen ── */}
      <button
        type="button"
        className={s.addBtn}
        onClick={addDoc}
      >
        + weiteres Dokument hinzufügen
      </button>
    </div>
  );
};
